import { useCallback, useMemo, useState } from 'react';
import { Props } from './types';
import { CancelReasonType } from 'domains/models/subscription/cancel_reason/cancel_reason_type';

const minStep = 0;
const maxStep = 2;

const askForDetailCancelReason = [
  CancelReasonType.AnotherProduct,
  CancelReasonType.LessFeatures,
  CancelReasonType.LowUsage,
  CancelReasonType.Expensive,
  CancelReasonType.Other,
];

export const useCancelQuestionDialog = (props: Props) => {
  const [currentStep, setCurrentStep] = useState(minStep);
  const [cancelReasonTypes, setCancelReasonTypes] = useState<
    CancelReasonType[]
  >([]);
  const [askForDetail, setAskForDetail] = useState<{ [key: number]: string }>(
    {},
  );
  const [feedback, setFeedback] = useState('');

  const canNextStep = useMemo(() => {
    switch (currentStep) {
      case 0:
        if (cancelReasonTypes.length < 1) {
          return false;
        } else if (Object.keys(askForDetail).length > 0) {
          let isAnyEmpty = false;
          for (const key of Object.keys(askForDetail)) {
            const reason = Number(key);
            if (
              askForDetailCancelReason.includes(reason) &&
              !askForDetail[reason]
            ) {
              isAnyEmpty = true;
            }
          }
          if (isAnyEmpty) {
            return false;
          }
        }
        break;
      case 1:
        if (!feedback) {
          return false;
        }
        break;
      case 2:
        return true;
      default:
        return false;
    }

    return true;
  }, [currentStep, cancelReasonTypes, askForDetail, feedback]);

  const onClickNext = useCallback(() => {
    if (currentStep < maxStep) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === maxStep) {
      props.onSubmit({
        reasons: cancelReasonTypes.map((reason) => ({
          reason,
          detail: askForDetail[reason],
        })),
        feedback,
      });
    }
  }, [currentStep, cancelReasonTypes, askForDetail, feedback]);

  const onClickBack = useCallback(() => {
    if (minStep < currentStep) {
      setCurrentStep(currentStep - 1);
    } else {
      props.onCancel();
    }
  }, [currentStep]);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      switch (e.target.name) {
        case 'cancelReason':
          const reason = Number(e.target.value) as CancelReasonType;
          if (cancelReasonTypes.includes(reason)) {
            setCancelReasonTypes(cancelReasonTypes.filter((r) => r !== reason));
            delete askForDetail[reason];
            setAskForDetail({ ...askForDetail });
          } else {
            setCancelReasonTypes([...cancelReasonTypes, reason]);
            if (!askForDetail[reason]) {
              const newAskForDetail = {
                ...askForDetail,
                [reason]: '',
              };
              setAskForDetail(newAskForDetail);
            }
          }
          break;
        case 'askForDetail':
          const reasonFromId = Number(e.target.id.replace('cancelReason', ''));
          const newAskForDetail = {
            ...askForDetail,
            [reasonFromId]: e.target.value,
          };
          setAskForDetail(newAskForDetail);
          break;
        case 'cancelFeedback':
          setFeedback(e.target.value);
          break;
        default:
          break;
      }
    },
    [cancelReasonTypes, askForDetail],
  );

  const submitText =
    currentStep === maxStep
      ? props.isLeave
        ? 'submit leave'
        : 'submit'
      : 'next';

  const cancelText = currentStep === minStep ? 'cancel' : 'back';

  const isShowConfirmLeaveText = currentStep === maxStep && props.isLeave;

  return {
    state: {
      canNextStep,
      maxStep,
      currentStep,
      submitText,
      cancelText,
      cancelReasonTypes,
      askForDetail,
      feedback,
      askForDetailCancelReason,
      isShowConfirmLeaveText,
    },
    actions: {
      onClickNext,
      onClickBack,
      onChange,
    },
  };
};
